import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { JobService } from '@app/services/job/job.service';
import { Router } from '@angular/router';
import { PendingApprovalSummary, summarizePendingApprovalJobs } from '../../models/pending-approval.model';

@Component({
  selector: 'app-pending-approval-jobs-summary',
  templateUrl: './pending-approval-jobs-summary.component.html',
  styleUrl: './pending-approval-jobs-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PendingApprovalJobsSummaryComponent implements OnInit, OnDestroy {
  private readonly navigationService = inject(NavigationService);
  private readonly router = inject(Router);
  private readonly jobService = inject(JobService);
  readonly siteName = signal('');
  readonly brandName = signal('');
  readonly summaries = signal<PendingApprovalSummary[]>([]);
  readonly isLoading = signal(true);
  readonly hasLoadError = signal(false);
  readonly brands = computed(() => [...new Set(
    this.summaries()
      .map(summary => summary.brandName)
      .filter((brand): brand is string => !!brand)
  )].sort());
  readonly filteredSummaries = computed(() => {
    const siteName = this.siteName().trim().toLowerCase();
    const brandName = this.brandName();
    return this.summaries().filter(summary =>
      (!siteName || (summary.siteName ?? '').toLowerCase().includes(siteName)) &&
      (!brandName || summary.brandName === brandName));
  });

  constructor() {
    this.navigationService.onChange(false, false);
  }

  async ngOnInit(): Promise<void> {
    try {
      const jobs = await firstValueFrom(this.jobService.getJobsPendingApproval());
      this.summaries.set(summarizePendingApprovalJobs(jobs ?? []));
    } catch {
      this.hasLoadError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  openJobList(summary: PendingApprovalSummary, jobType: 'New' | 'PPM'): void {
    void this.router.navigate(['/pendingapprovaljob/list'], {
      queryParams: {
        siteId: summary.siteId,
        siteName: summary.siteName ?? '',
        jobType
      }
    });
  }

  ngOnDestroy(): void {
    this.navigationService.onChange(true, false);
  }
}
